import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import type { RootState } from 'store';
import { createConnectionId } from 'util/createConnectionId';
import type {
  AddConnectionActionInterface,
  EditConnectionActionInterface,
  RemoveConnectionActionInterface,
  RemoveConnectionsActionInterface,
  SetConnectionColorActionInterface,
  UpdateConnectionPointsAction,
} from './sagaActions';
import { getConnections } from './selectors';
import type { ConnectionInterface } from './types';
import {
  ADD_CONNECTION,
  EDIT_CONNECTION,
  REMOVE_CONNECTION,
  REMOVE_CONNECTIONS,
  SET_CONNECTION_COLOR,
  UPDATE_CONNECTION_POINTS,
} from './types';
import { setConnections } from 'store/units/reducer';

export function* addConnection(action: AddConnectionActionInterface) {
  try {
    const { connections } = yield select(
      (state: RootState) => state.unitConnGroup.present.connectionState,
    );

    const id = createConnectionId(
      action.payload.portkeys.fromPort,
      action.payload.portkeys.toPort,
      action.payload.key,
    );

    const newConnection: ConnectionInterface = {
      key: id,
      from: action.payload.portkeys.from,
      to: action.payload.portkeys.to,
      fromPort: action.payload.portkeys.fromPort,
      toPort: action.payload.portkeys.toPort,
      conveyorCalc: false,
      ...(action.payload.connectionPoints
        ? { connectionPoints: action.payload.connectionPoints }
        : {}),
    };
    const newConnections = [...connections, newConnection];
    yield put(setConnections(newConnections));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'connections' },
    });
    console.error(e);
  }
}

export function* removeConnection(action: RemoveConnectionActionInterface) {
  try {
    const { connections } = yield select(
      (state: RootState) => state.unitConnGroup.present.connectionState,
    );

    const portKeysToRemove = Array.isArray(action.payload)
      ? action.payload
      : [action.payload];
    const newConnections = connections.filter(
      (connection: ConnectionInterface) =>
        !portKeysToRemove.some(
          (portKeys) =>
            connection.fromPort === portKeys.fromPort &&
            connection.toPort === portKeys.toPort,
        ),
    );

    if (newConnections.length !== connections.length) {
      yield put(setConnections(newConnections));
    }
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'connections' },
    });
    console.error(e);
  }
}

function* removeConnections(action: RemoveConnectionsActionInterface) {
  const connections: ConnectionInterface[] = yield select(getConnections);
  const unitKey = action.payload;
  const modifiedConnections = connections.filter(
    (conn) => conn.to !== unitKey && conn.from !== unitKey,
  );
  yield put(setConnections(modifiedConnections));
}

export function* editConnection(action: EditConnectionActionInterface) {
  try {
    const { connections }: { connections: ConnectionInterface[] } =
      yield select(
        (state: RootState) => state.unitConnGroup.present.connectionState,
      );

    let modifiedConnections: ConnectionInterface[] | undefined;

    action.payload.forEach((editConnectionPayload) => {
      const editFromIndex = connections.findIndex(
        (c: ConnectionInterface) =>
          c.fromPort === editConnectionPayload.fromPort,
      );
      const editToIndex = connections.findIndex(
        (c: ConnectionInterface) => c.toPort === editConnectionPayload.toPort,
      );

      const editIndex =
        editConnectionPayload.portMode === 'linkToPortId'
          ? editFromIndex
          : editConnectionPayload.portMode === 'linkFromPortId'
            ? editToIndex
            : -1;

      if (editIndex !== -1) {
        const connection = connections[editIndex];
        const modifiedConnection = {
          ...connection,
          fromPort: editConnectionPayload.fromPort,
          toPort: editConnectionPayload.toPort,
          to: editConnectionPayload.to,
          from: editConnectionPayload.from,
          flowRatePosition: editConnectionPayload.flowRatePosition,
          key: `${connection.fromPort}${editConnectionPayload.toPort}`,
        };
        modifiedConnections = modifiedConnections ?? [...connections];
        modifiedConnections.splice(editIndex, 1, modifiedConnection);
      }
    });
    if (modifiedConnections) {
      yield put(setConnections(modifiedConnections));
    }
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'connections' },
    });
    console.error(e);
  }
}
export function* updateConnectionPoints(action: UpdateConnectionPointsAction) {
  try {
    const { connections } = yield select(
      (state: RootState) => state.unitConnGroup.present.connectionState,
    );

    const editIndex = connections.findIndex(
      (connection: ConnectionInterface) =>
        connection.key ===
        action.payload.updatedConnectionPointsPayload.connectionKey,
    );
    if (editIndex !== -1) {
      const connection = connections[editIndex];
      const modifiedConncetion = {
        ...connection,
        connectionPoints:
          action.payload.updatedConnectionPointsPayload.updateConnectionPoints,
      };
      const modifiedConncetions = [...connections];
      modifiedConncetions.splice(editIndex, 1, modifiedConncetion);
      yield put(setConnections(modifiedConncetions));
    }
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'connections' },
    });
    console.error(e);
  }
}

export function* setConnectionColor(action: SetConnectionColorActionInterface) {
  try {
    const { connections }: { connections: ConnectionInterface[] } =
      yield select(
        (state: RootState) => state.unitConnGroup.present.connectionState,
      );

    const connectionKeys = new Set(action.payload.connectionKeys);
    const modifiedConnections = connections.map((connection) => {
      if (!connectionKeys.has(connection.key)) {
        return connection;
      }

      const modifiedConnection: ConnectionInterface = { ...connection };
      if (action.payload.color) {
        modifiedConnection.color = action.payload.color;
      } else {
        delete modifiedConnection.color;
      }
      return modifiedConnection;
    });

    if (
      modifiedConnections.some(
        (connection, index) => connection !== connections[index],
      )
    ) {
      yield put(setConnections(modifiedConnections));
    }
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'connections' },
    });
    console.error(e);
  }
}

function* connectionSagas() {
  yield takeEvery(ADD_CONNECTION, addConnection);
  yield takeEvery(REMOVE_CONNECTION, removeConnection);
  yield takeEvery(REMOVE_CONNECTIONS, removeConnections);
  yield takeEvery(EDIT_CONNECTION, editConnection);
  yield takeLatest(UPDATE_CONNECTION_POINTS, updateConnectionPoints);
  yield takeEvery(SET_CONNECTION_COLOR, setConnectionColor);
}

export default connectionSagas;
